'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div data-component-id="src/components/common/ErrorBoundary.tsx:32:10" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="32" data-component-end-line="61" data-component-file="ErrorBoundary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20flex%20items-center%20justify-center%20bg-bg-main%22%7D" className="min-h-screen flex items-center justify-center bg-bg-main">
            <div data-component-id="src/components/common/ErrorBoundary.tsx:33:12" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="33" data-component-end-line="60" data-component-file="ErrorBoundary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-md%20w-full%20mx-auto%20p-8%20text-center%22%7D" className="max-w-md w-full mx-auto p-8 text-center">
              <div data-component-id="src/components/common/ErrorBoundary.tsx:34:14" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="34" data-component-end-line="49" data-component-file="ErrorBoundary.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%22%7D" className="mb-4">
                <svg data-component-id="src/components/common/ErrorBoundary.tsx:35:16" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="35" data-component-end-line="48" data-component-file="ErrorBoundary.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22mx-auto%20h-12%20w-12%20text-red-500%22%7D"
                  className="mx-auto h-12 w-12 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path data-component-id="src/components/common/ErrorBoundary.tsx:42:18" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="42" data-component-end-line="47" data-component-file="ErrorBoundary.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 data-component-id="src/components/common/ErrorBoundary.tsx:50:14" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="50" data-component-end-line="50" data-component-file="ErrorBoundary.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Something%20went%20wrong%22%7D" className="text-2xl font-bold text-text-primary mb-2">Something went wrong</h2>
              <p data-component-id="src/components/common/ErrorBoundary.tsx:51:14" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="51" data-component-end-line="53" data-component-file="ErrorBoundary.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20mb-4%22%2C%22textContent%22%3A%22We're%20sorry%2C%20but%20something%20unexpected%20happened.%20Please%20try%20refreshing%20the%20page.%22%7D" className="text-text-secondary mb-4">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
              <button data-component-id="src/components/common/ErrorBoundary.tsx:54:14" data-component-path="src/components/common/ErrorBoundary.tsx" data-component-line="54" data-component-end-line="59" data-component-file="ErrorBoundary.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-primary%20text-white%20px-4%20py-2%20rounded-lg%20hover%3Abg-primary-dark%20transition-colors%22%2C%22textContent%22%3A%22Refresh%20Page%22%7D"
                onClick={() => window.location.reload()}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
